import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { resolveMediaUrl } from '../../config/api';

const ACCENT = '#2e9f86';
const inp = { border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' };

// ─── shared queries hook ───────────────────────────────────────────────────────
function useSharedData() {
  const { data: allProducts = [], isLoading } = useQuery({
    queryKey: ['products-all'],
    queryFn: () => api.get('/api/products', { params: { all: true } }).then(r => Array.isArray(r.data) ? r.data : (r.data?.data || [])),
  });
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get('/api/categories').then(r => r.data),
  });
  const { data: modifierGroups = [] } = useQuery({
    queryKey: ['modifier-groups'],
    queryFn: () => api.get('/api/modifier-groups').then(r => r.data),
  });
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get('/api/settings').then(r => r.data),
  });
  const productions  = allProducts.filter(p => p.type === 'production');
  const rawMaterials = allProducts.filter(p => p.sellable === false);
  const rate = settings?.exchange_rate || 89500;
  return { productions, rawMaterials, categories, modifierGroups, rate, isLoading };
}

// ─── main page ─────────────────────────────────────────────────────────────────
export default function ProductionList() {
  const [section, setSection] = useState('production'); // 'production' | 'raw'
  const { productions, rawMaterials, categories, modifierGroups, rate, isLoading } = useSharedData();

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* sub-tab bar */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 20, background: '#f3f4f6', borderRadius: 10, padding: 4, alignSelf: 'flex-start' }}>
        {[{ key: 'production', label: '🏭 Production Items' }, { key: 'raw', label: '🥕 Raw Materials' }].map(s => (
          <button key={s.key} onClick={() => setSection(s.key)} style={{
            background: section === s.key ? '#fff' : 'transparent',
            color: section === s.key ? ACCENT : '#6b7280',
            border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 13,
            fontWeight: section === s.key ? 700 : 500, cursor: 'pointer',
            boxShadow: section === s.key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.15s',
          }}>{s.label}</button>
        ))}
      </div>

      {section === 'production'
        ? <ProductionSection productions={productions} rawMaterials={rawMaterials} categories={categories} modifierGroups={modifierGroups} rate={rate} isLoading={isLoading} />
        : <RawMaterialSection rawMaterials={rawMaterials} categories={categories} rate={rate} isLoading={isLoading} />
      }
    </div>
  );
}

// ─── Production Items section ──────────────────────────────────────────────────
function ProductionSection({ productions, rawMaterials, categories, modifierGroups, rate, isLoading }) {
  const queryClient = useQueryClient();
  const fileRef = useRef(null);

  const [panelOpen,        setPanelOpen]        = useState(false);
  const [editing,          setEditing]          = useState(null);
  const [name,             setName]             = useState('');
  const [priceUsd,         setPriceUsd]         = useState('');
  const [categoryId,       setCategoryId]       = useState('');
  const [imageUrl,         setImageUrl]         = useState('');
  const [uploading,        setUploading]        = useState(false);
  const [ingredients,      setIngredients]      = useState([]);
  const [ingSearch,        setIngSearch]        = useState('');
  const [recipeSynced,     setRecipeSynced]     = useState(false);
  const [modifierGroupIds, setModifierGroupIds] = useState([]);
  const [error,            setError]            = useState('');

  const autoCost = ingredients.reduce((sum, ing) => {
    const mat = rawMaterials.find(m => String(m._id) === String(ing.product_id));
    if (!mat || !mat.cost) return sum;
    const costUSD = mat.cost_currency && mat.cost_currency !== 'USD' ? (Number(mat.cost) || 0) / rate : (Number(mat.cost) || 0);
    return sum + costUSD * Number(ing.qty || 0);
  }, 0);

  const { data: editingRecipe } = useQuery({
    queryKey: ['recipe', editing?._id],
    queryFn: () => api.get(`/api/recipes/${editing._id}`).then(r => r.data),
    enabled: !!editing?._id,
  });
  useEffect(() => {
    if (editingRecipe?.ingredients && !recipeSynced) {
      setIngredients(editingRecipe.ingredients.map(i => ({ ...i, product_id: String(i.product_id) })));
      setRecipeSynced(true);
    }
  }, [editingRecipe, recipeSynced]);

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post('/api/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImageUrl(res.data.url || res.data.path || '');
    } catch { setError('Image upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const saveMutation = useMutation({
    mutationFn: async ({ name, priceUsd, categoryId, imageUrl, modifierGroupIds, ingredients, editingId }) => {
      let productId = editingId;
      const payload = {
        name,
        price_usd: Number(priceUsd) || 0,
        price_lbp: Math.round((Number(priceUsd) || 0) * rate),
        category_id: categoryId || null,
        image_url: imageUrl || null,
        modifier_group_ids: modifierGroupIds,
      };
      if (editingId) {
        await api.put(`/api/products/${editingId}`, payload);
      } else {
        const res = await api.post('/api/products', { ...payload, type: 'production', manage_stock: false });
        productId = res.data._id;
      }
      await api.put(`/api/recipes/${productId}`, { ingredients });
      return productId;
    },
    onSuccess: () => { queryClient.invalidateQueries(['products-all']); queryClient.invalidateQueries(['products']); queryClient.invalidateQueries(['products-pos']); closePanel(); },
    onError: (err) => setError(err.response?.data?.error || 'Error saving'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/products/${id}`),
    onSuccess: () => { queryClient.invalidateQueries(['products-all']); queryClient.invalidateQueries(['products']); queryClient.invalidateQueries(['products-pos']); },
  });

  const reset = () => { setEditing(null); setName(''); setPriceUsd(''); setCategoryId(''); setImageUrl(''); setIngredients([]); setIngSearch(''); setModifierGroupIds([]); setError(''); setRecipeSynced(false); };
  const openNew  = () => { reset(); setPanelOpen(true); };
  const openEdit = (p) => { reset(); setEditing(p); setName(p.name); setPriceUsd(p.price_usd || ''); setCategoryId(p.category_id ? String(p.category_id) : ''); setImageUrl(p.image_url || ''); setModifierGroupIds((p.modifier_group_ids || []).map(String)); setPanelOpen(true); };
  const closePanel = () => { reset(); setPanelOpen(false); };

  const addIngredient    = (mat) => { setIngredients(l => [...l, { product_id: String(mat._id), qty: 1, unit: mat.unit || 'piece' }]); setIngSearch(''); };
  const removeIngredient = (idx) => setIngredients(l => l.filter((_, i) => i !== idx));
  const updateIngredient = (idx, f, v) => setIngredients(l => l.map((x, i) => i === idx ? { ...x, [f]: v } : x));
  const filteredMats = rawMaterials.filter(m => m.name?.toLowerCase().includes(ingSearch.toLowerCase()) && !ingredients.some(i => String(i.product_id) === String(m._id)));

  return (
    <div style={{ position: 'relative', flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: '#6b7280' }}>{productions.length} item{productions.length !== 1 ? 's' : ''}</div>
        <button onClick={openNew} style={{ background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>+ New Production Item</button>
      </div>

      {isLoading ? <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>Loading…</div>
        : productions.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: 60 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🏭</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>No production items yet</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Create production items with recipes from raw materials</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
            {productions.map(p => {
              const imgSrc = resolveMediaUrl(p.image_url);
              const cat = categories.find(c => String(c._id) === String(p.category_id));
              return (
                <div key={p._id} style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                  {imgSrc
                    ? <img src={imgSrc} alt={p.name} style={{ width: '100%', height: 130, objectFit: 'cover', display: 'block' }} />
                    : <div style={{ width: '100%', height: 80, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>🏭</div>
                  }
                  <div style={{ padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#111' }}>{p.name}</div>
                        {cat && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>📂 {cat.name}</div>}
                      </div>
                      <div style={{ display: 'flex', gap: 5, flexShrink: 0, marginLeft: 8 }}>
                        <button onClick={() => openEdit(p)} style={{ background: '#eff6ff', border: 'none', borderRadius: 6, padding: '4px 9px', fontSize: 11, fontWeight: 600, color: '#2563eb', cursor: 'pointer' }}>Edit</button>
                        <button onClick={() => { if (window.confirm(`Delete "${p.name}"?`)) deleteMutation.mutate(p._id); }} style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '4px 9px', fontSize: 11, fontWeight: 600, color: '#b91c1c', cursor: 'pointer' }}>Delete</button>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      <span style={{ background: '#f0fdf8', color: '#065f46', borderRadius: 5, padding: '2px 6px', fontWeight: 600 }}>${Number(p.price_usd || 0).toFixed(2)}</span>
                      <span style={{ background: '#faf5ff', color: '#7c3aed', borderRadius: 5, padding: '2px 6px', fontWeight: 600 }}>Cost: ${Number(p.cost || 0).toFixed(2)}</span>
                      {(p.modifier_group_ids || []).length > 0 && (
                        <span style={{ background: '#fff7ed', color: '#c2410c', borderRadius: 5, padding: '2px 6px', fontWeight: 600 }}>+ Add-ons</span>
                      )}
                    </div>
                    <RecipeIngredients productId={p._id} rawMaterials={rawMaterials} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {/* overlay */}
      {panelOpen && <div onClick={closePanel} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 40, backdropFilter: 'blur(2px)' }} />}

      {/* panel */}
      {panelOpen && (
        <div style={{ position: 'fixed', top: 0, right: 0, width: 480, height: '100%', background: '#fff', zIndex: 50, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1.5px solid #f0f0f0' }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{editing ? 'Edit Production Item' : 'New Production Item'}</div>
            <button onClick={closePanel} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', fontSize: 18, lineHeight: 1, color: '#6b7280' }}>✕</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
            {error && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>{error}</div>}

            {/* photo */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Photo</label>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoChange} />
              {imageUrl ? (
                <div style={{ position: 'relative' }}>
                  <img src={resolveMediaUrl(imageUrl)} alt="" style={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 10, display: 'block', border: '1.5px solid #e5e7eb' }} />
                  <button onClick={() => setImageUrl('')} style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', borderRadius: 6, padding: '3px 8px', fontSize: 12, cursor: 'pointer' }}>Remove</button>
                </div>
              ) : (
                <div onClick={() => !uploading && fileRef.current?.click()}
                  style={{ border: '2px dashed #d1d5db', borderRadius: 10, height: 110, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: uploading ? 'wait' : 'pointer', color: '#9ca3af', gap: 6 }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = ACCENT}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#d1d5db'}>
                  <span style={{ fontSize: 26 }}>📷</span>
                  <span style={{ fontSize: 12 }}>{uploading ? 'Uploading…' : 'Click to upload photo'}</span>
                </div>
              )}
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Name *</label>
              <input value={name} onChange={e => setName(e.target.value)} style={inp} placeholder="e.g. Pizza Margherita" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Selling Price (USD)</label>
              <input type="number" min="0" step="0.01" value={priceUsd} onChange={e => setPriceUsd(e.target.value)} style={{ ...inp, borderColor: ACCENT, background: '#f0fdf8' }} placeholder="0.00" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Category</label>
              <select value={categoryId} onChange={e => setCategoryId(e.target.value)} style={{ ...inp, background: '#fafafa' }}>
                <option value="">— No category —</option>
                {categories.map(c => <option key={c._id} value={String(c._id)}>{c.name}</option>)}
              </select>
            </div>

            {/* add-on groups */}
            {modifierGroups.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Add-on Groups</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {modifierGroups.map(g => {
                    const on = modifierGroupIds.includes(String(g._id));
                    return (
                      <button key={g._id} type="button"
                        onClick={() => setModifierGroupIds(ids => on ? ids.filter(id => id !== String(g._id)) : [...ids, String(g._id)])}
                        style={{
                          border: `2px solid ${on ? ACCENT : '#e5e7eb'}`,
                          background: on ? '#f0fdf8' : '#fafafa',
                          color: on ? '#065f46' : '#374151',
                          borderRadius: 8, padding: '5px 12px', fontSize: 12,
                          fontWeight: on ? 700 : 500, cursor: 'pointer',
                        }}>
                        {on ? '✓ ' : ''}{g.name}
                      </button>
                    );
                  })}
                </div>
                {modifierGroupIds.length > 0 && (
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
                    {modifierGroupIds.length} group{modifierGroupIds.length !== 1 ? 's' : ''} linked — customers will choose add-ons at order time
                  </div>
                )}
              </div>
            )}

            {/* ingredients */}
            <div style={{ borderTop: '1.5px dashed #e5e7eb', paddingTop: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>🥕 Ingredients</div>
              {ingredients.length === 0 && <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 10 }}>No ingredients yet — search below</div>}
              {ingredients.map((ing, idx) => {
                const mat = rawMaterials.find(m => String(m._id) === String(ing.product_id));
                return (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#374151', background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 7, padding: '7px 10px' }}>
                      🥕 {mat?.name || ing.product_id}
                      {mat?.cost ? <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 6 }}>${Number(mat.cost).toFixed(3)}/{mat.unit || 'pc'}</span> : null}
                    </div>
                    <input type="number" min="0.001" step="any" value={ing.qty} onChange={e => updateIngredient(idx, 'qty', e.target.value)} style={{ ...inp, width: 70 }} />
                    <select value={ing.unit} onChange={e => updateIngredient(idx, 'unit', e.target.value)} style={{ ...inp, width: 68 }}>
                      {['piece', 'kg', 'g', 'L', 'ml', 'lb', 'box'].map(u => <option key={u}>{u}</option>)}
                    </select>
                    <button onClick={() => removeIngredient(idx)} style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '7px 9px', cursor: 'pointer', color: '#b91c1c', fontSize: 14, fontWeight: 700, flexShrink: 0 }}>✕</button>
                  </div>
                );
              })}
              <input value={ingSearch} onChange={e => setIngSearch(e.target.value)} placeholder="Search raw material to add…" style={{ ...inp, marginTop: 8 }} />
              {ingSearch.trim() && (
                <div style={{ border: '1.5px solid #e5e7eb', borderRadius: 8, marginTop: 4, maxHeight: 180, overflowY: 'auto', background: '#fff' }}>
                  {filteredMats.length === 0
                    ? <div style={{ padding: '8px 12px', fontSize: 12, color: '#9ca3af' }}>No raw materials found</div>
                    : filteredMats.map(m => (
                      <div key={m._id} onClick={() => addIngredient(m)}
                        style={{ padding: '9px 12px', cursor: 'pointer', fontSize: 13, color: '#374151', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f0fdf8'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                        <span>🥕 {m.name}</span>
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>{m.unit || 'piece'} · ${Number(m.cost || 0).toFixed(3)}</span>
                      </div>
                    ))}
                </div>
              )}
              {ingredients.length > 0 && (
                <div style={{ marginTop: 12, background: '#faf5ff', border: '1.5px solid #e9d5ff', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
                  <span style={{ fontWeight: 600, color: '#7c3aed' }}>Auto Cost: </span>
                  <span style={{ fontWeight: 700 }}>${autoCost.toFixed(3)}</span>
                  {priceUsd && <span style={{ marginLeft: 12, color: '#059669', fontWeight: 600 }}>Margin: ${(Number(priceUsd) - autoCost).toFixed(2)}</span>}
                </div>
              )}
            </div>
          </div>
          <div style={{ padding: '14px 20px', borderTop: '1.5px solid #f0f0f0', display: 'flex', gap: 10 }}>
            <button onClick={closePanel} style={{ flex: 1, background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, padding: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            <button onClick={() => { if (!name.trim()) { setError('Name is required'); return; } saveMutation.mutate({ name: name.trim(), priceUsd, categoryId, imageUrl, modifierGroupIds, ingredients, editingId: editing?._id }); }}
              disabled={saveMutation.isPending || uploading}
              style={{ flex: 2, background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: (saveMutation.isPending || uploading) ? 0.7 : 1 }}>
              {saveMutation.isPending ? 'Saving…' : editing ? 'Save Changes' : 'Create Production Item'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Raw Materials section ─────────────────────────────────────────────────────
function RawMaterialSection({ rawMaterials, categories, rate, isLoading }) {
  const queryClient = useQueryClient();

  const [panelOpen,  setPanelOpen]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [name,       setName]       = useState('');
  const [unit,       setUnit]       = useState('piece');
  const [cost,       setCost]       = useState('');
  const [costCur,    setCostCur]    = useState('USD');
  const [categoryId, setCategoryId] = useState('');
  const [stock,      setStock]      = useState('');
  const [error,      setError]      = useState('');

  const saveMutation = useMutation({
    mutationFn: ({ name, unit, cost, costCur, categoryId, stock, editingId }) => {
      const payload = { name, unit, cost: Number(cost) || 0, cost_currency: costCur, category_id: categoryId || null, stock: Number(stock) || 0, sellable: false, type: 'simple', manage_stock: true };
      return editingId ? api.put(`/api/products/${editingId}`, payload) : api.post('/api/products', payload);
    },
    onSuccess: () => { queryClient.invalidateQueries(['products-all']); queryClient.invalidateQueries(['products']); closePanel(); },
    onError: (err) => setError(err.response?.data?.error || 'Error saving'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/products/${id}`),
    onSuccess: () => { queryClient.invalidateQueries(['products-all']); queryClient.invalidateQueries(['products']); },
  });

  const reset = () => { setEditing(null); setName(''); setUnit('piece'); setCost(''); setCostCur('USD'); setCategoryId(''); setStock(''); setError(''); };
  const openNew  = () => { reset(); setPanelOpen(true); };
  const openEdit = (p) => {
    reset();
    setEditing(p); setName(p.name); setUnit(p.unit || 'piece'); setCost(p.cost || ''); setCostCur(p.cost_currency || 'USD');
    setCategoryId(p.category_id ? String(p.category_id) : ''); setStock(p.stock ?? '');
    setPanelOpen(true);
  };
  const closePanel = () => { reset(); setPanelOpen(false); };

  return (
    <div style={{ position: 'relative', flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: '#6b7280' }}>{rawMaterials.length} material{rawMaterials.length !== 1 ? 's' : ''}</div>
        <button onClick={openNew} style={{ background: '#92400e', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>+ New Raw Material</button>
      </div>

      {isLoading ? <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>Loading…</div>
        : rawMaterials.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: 60 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🥕</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>No raw materials yet</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Raw materials are ingredients used in production recipes</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                {['Name', 'Unit', 'Cost', 'Stock', 'Category', ''].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1.5px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rawMaterials.map((m, i) => {
                const cat = categories.find(c => String(c._id) === String(m.category_id));
                const costUSD = m.cost_currency && m.cost_currency !== 'USD' ? (Number(m.cost) || 0) / rate : (Number(m.cost) || 0);
                return (
                  <tr key={m._id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: '#111' }}>🥕 {m.name}</td>
                    <td style={{ padding: '10px 12px', color: '#6b7280' }}>{m.unit || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ fontWeight: 600, color: '#7c3aed' }}>${costUSD.toFixed(3)}</span>
                      {m.cost_currency !== 'USD' && <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 4 }}>({m.cost_currency})</span>}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#374151' }}>{m.stock ?? 0}</td>
                    <td style={{ padding: '10px 12px', color: '#6b7280' }}>{cat?.name || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => openEdit(m)} style={{ background: '#eff6ff', border: 'none', borderRadius: 6, padding: '4px 9px', fontSize: 11, fontWeight: 600, color: '#2563eb', cursor: 'pointer' }}>Edit</button>
                        <button onClick={() => { if (window.confirm(`Delete "${m.name}"?`)) deleteMutation.mutate(m._id); }} style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '4px 9px', fontSize: 11, fontWeight: 600, color: '#b91c1c', cursor: 'pointer' }}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

      {/* overlay */}
      {panelOpen && <div onClick={closePanel} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 40, backdropFilter: 'blur(2px)' }} />}

      {/* panel */}
      {panelOpen && (
        <div style={{ position: 'fixed', top: 0, right: 0, width: 420, height: '100%', background: '#fff', zIndex: 50, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1.5px solid #f0f0f0' }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{editing ? 'Edit Raw Material' : 'New Raw Material'}</div>
            <button onClick={closePanel} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', fontSize: 18, lineHeight: 1, color: '#6b7280' }}>✕</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
            {error && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>{error}</div>}

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Name *</label>
              <input value={name} onChange={e => setName(e.target.value)} style={inp} placeholder="e.g. Mozzarella Cheese" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Unit</label>
                <select value={unit} onChange={e => setUnit(e.target.value)} style={{ ...inp, background: '#fafafa' }}>
                  {['piece', 'kg', 'g', 'L', 'ml', 'lb', 'box', 'pack'].map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Stock</label>
                <input type="number" min="0" step="any" value={stock} onChange={e => setStock(e.target.value)} style={inp} placeholder="0" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Cost</label>
                <input type="number" min="0" step="any" value={cost} onChange={e => setCost(e.target.value)} style={{ ...inp, borderColor: '#7c3aed', background: '#faf5ff' }} placeholder="0.000" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Currency</label>
                <select value={costCur} onChange={e => setCostCur(e.target.value)} style={{ ...inp, background: '#fafafa' }}>
                  <option value="USD">USD</option>
                  <option value="LBP">LBP</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Category</label>
              <select value={categoryId} onChange={e => setCategoryId(e.target.value)} style={{ ...inp, background: '#fafafa' }}>
                <option value="">— No category —</option>
                {categories.map(c => <option key={c._id} value={String(c._id)}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ padding: '14px 20px', borderTop: '1.5px solid #f0f0f0', display: 'flex', gap: 10 }}>
            <button onClick={closePanel} style={{ flex: 1, background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, padding: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            <button onClick={() => { if (!name.trim()) { setError('Name is required'); return; } saveMutation.mutate({ name: name.trim(), unit, cost, costCur, categoryId, stock, editingId: editing?._id }); }}
              disabled={saveMutation.isPending}
              style={{ flex: 2, background: '#92400e', color: '#fff', border: 'none', borderRadius: 8, padding: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: saveMutation.isPending ? 0.7 : 1 }}>
              {saveMutation.isPending ? 'Saving…' : editing ? 'Save Changes' : 'Create Raw Material'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── recipe ingredients mini-list ──────────────────────────────────────────────
function RecipeIngredients({ productId, rawMaterials }) {
  const { data: recipe } = useQuery({
    queryKey: ['recipe', productId],
    queryFn: () => api.get(`/api/recipes/${productId}`).then(r => r.data),
  });
  if (!recipe?.ingredients?.length) return <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>No recipe yet</div>;
  return (
    <div style={{ marginTop: 6 }}>
      {recipe.ingredients.map((ing, i) => {
        const mat = rawMaterials.find(m => String(m._id) === String(ing.product_id));
        return (
          <div key={i} style={{ fontSize: 11, color: '#374151', padding: '2px 0', display: 'flex', justifyContent: 'space-between' }}>
            <span>🥕 {mat?.name || '—'}</span>
            <span style={{ color: '#6b7280' }}>{ing.qty} {ing.unit}</span>
          </div>
        );
      })}
    </div>
  );
}
